import { 
  Avatar, 
  Backdrop, 
  Box, 
  Button,
  Chip, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Grid, 
  IconButton, 
  Link, 
  Paper,
  Stack,
  Tooltip,
  Typography 
} from "@mui/material";
import { useMemo, useState } from "react";
import { getUUID } from "src/utils/url";
import { fhirClient } from "src/App";
import { useSnackbar } from "notistack";
import FhirOrganizationGrid, { ActionsFn, filterOrgBySearchInput } from "../common/FhirOrganizationGrid";
import { cleanSearchInput, trimMultipleWhiteSpaces } from "src/utils/string";
import { WarningIcon } from "src/theme/overrides/CustomIcons";
import SearchTextField from "src/components/SearchTextField";
import usePatientSummary from "src/hooks/usePatientSummary";
import Iconify from "src/components/Iconify";
import { getColorByKey, getGoToSection } from "../common/util";
import { useOrganizations } from "src/careflow/ph-core/fhir-client/fhir-react";
import { OrganizationWrapper, WrappedOrganization } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization";
import useGridFilters from "src/careflow/ph-core/grid/hook/useGridFilters";
import { FhirResource } from "src/careflow/ph-core/fhir-client/resources";

type Props = {
  open: boolean;
  onClose: () => void;
  patient: any;
  onConfirm: () => void;
  translate: any;
  onSelectRow: any;
};

const defaultIcon = 'hugeicons:workflow-circle-06';

const TransferManagingOrg = ({
  open,
  onClose,
  patient,
  onConfirm,
  translate,
  onSelectRow
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();

  const [organizations, {
    isLoading: isLoadingOrg
  }] = useOrganizations({ 
      filter: { 'active:not': false },
      map: OrganizationWrapper, autofetch: !!open
    }
  );

  const patientId = (patient && patient?.indexedFhirRefUri) 
    ? getUUID(patient?.indexedFhirRefUri) 
    : null;

  const {
    data,
    loading: isLoadingSummary
  } = usePatientSummary(patientId, translate);

  const dataNoCount =
    Array.isArray(data) &&
    data.length > 0 &&
    data.every((e) => (e?.count ?? 0) === 0);

  const [ orgSelected, setOrgSelected ] = useState<WrappedOrganization | null>(null);
  const { searchTextFieldValue, onSearchTextFieldChange } = useGridFilters({ searchTextFieldValue: null });
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const orgsFiltered = useMemo(() => {
    const searchInput = cleanSearchInput(
      searchTextFieldValue ?? ''
    ).toLowerCase();
  
    return (organizations ?? [])
      .filter(org => filterOrgBySearchInput(searchInput, org));
  }, [organizations, searchTextFieldValue]);

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms || 300));
  }

  const confirmTransfer = async () => {
    try {
      setOpenBackdrop(true);

      const { id, patches } = {
        id: getUUID(patient?.indexedFhirRefUri),
        patches: [
          {
            op: 'replace',
            path: '/managingOrganization',
            value: {
              display: orgSelected?.name,
              reference: `${orgSelected?.resourceType}/${orgSelected?.id}`,
            },
          },
        ],
      };

      await fhirClient.patchOne<FhirResource>('Patient', id!, patches)

      await sleep(6000);
      onSelectRow(null);
      handlerClose();
      onConfirm();

    } catch (error) {
      console.error(error);
      enqueueSnackbar('An error has occurred', { variant: 'error' });
    } finally {
      setOpenBackdrop(false);
    }
  };

  const handlerClose = () => {
    setOrgSelected(null);
    onSelectRow(null);
    onClose();
  };

  const actions: ActionsFn = ({ row }) => {
    const isOrgSelected = orgSelected?.id === row?.id;

    return [
      <Button
        key={row?.id}
        onClick={() => setOrgSelected(row)}
        color={isOrgSelected ? "error": "primary"}
        variant="outlined"
      >
        {isOrgSelected
          ? translate('details.child.remove')
          : translate('details.child.select')
        }
      </Button>
    ];
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {translate('details.patient.transfer.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('details.patient.transfer.subTitle')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box m={2}>
          <Paper sx={{ p: 1, mb: 2, bgcolor: "#ffdad6" }}>
            <Box display="flex" gap={2} alignItems="center" color="#c83727">
              <WarningIcon sx={{ width: 32, height: 32, color: '#c83727' }}/>
              <Box>
                <Typography fontWeight={600}>
                  {translate('details.patient.transfer.notice')}
                </Typography>
                <Typography variant="body2">
                  {translate('details.patient.transfer.noticeNote')}
                </Typography>
              </Box>
            </Box>
          </Paper>
          {(!dataNoCount && !isLoadingSummary) && 
            <Paper sx={{ p: 1, mb: 2, bgcolor: "#ffe5002e" }}>
              <Box display="flex" gap={2} alignItems="center" color="#524a03">
                <WarningIcon sx={{ width: 32, height: 32, color: '#ffc301' }}/>
                <Box>
                  <Typography fontWeight={600}>
                    {translate('details.patient.transfer.warning')}
                  </Typography>
                  <Typography variant="body2">
                    {translate('details.patient.transfer.activeRecords')}
                  </Typography>
                </Box>
                <Link
                  onClick={() => {
                    document.getElementById("patient-summary")?.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                  sx={{ alignSelf: 'self-end' }}
                >
                  <Typography variant="body2">
                    {translate('details.patient.transfer.viewPatientSummary')}
                  </Typography>
                </Link>
              </Box>
            </Paper>
          }
          
          <Box p={2} bgcolor="#dae1e824" borderRadius="8px">
            <Grid container spacing={2}>
              {!isLoadingOrg ? (
                <Grid item xs={12}>
                  <FhirOrganizationGrid 
                    organizations={orgsFiltered}
                    searchTextFieldValue={searchTextFieldValue}
                    onSearchTextFieldChange={onSearchTextFieldChange}
                    translate={translate}
                    actions={actions}
                    filters={
                      <Box
                        sx={[
                          {
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row', 
                            alignItems: 'center',
                            position: 'relative',
                            justifyContent: 'space-between'
                          }
                        ]}
                      >
                        <Typography fontWeight={600} sx={{ marginLeft: "-20px"}}>
                          {translate('details.patient.transfer.newOrg')}
                        </Typography>
                        <SearchTextField
                          value={searchTextFieldValue}
                          onChange={(event) => {
                            onSearchTextFieldChange?.(trimMultipleWhiteSpaces(event.target.value));
                          }}
                          sx={{ marginRight: '-20px' }}
                        />
                      </Box>
                    }
                  />
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </Grid>
          </Box>
          {(!dataNoCount && !isLoadingSummary) && 
            <Box id="patient-summary">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ my: 2, ml: 2, fontWeight: 'bold' }}>
                  {translate('details.patient.transfer.patientSummary')}
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  mx: 2,
                  mb: 2,
                  gap: 1.5
                }}
              >
                {
                  data?.map((item, index) => (
                    <Paper
                      key={`${item?.scope}-00TY${index}`}
                      elevation={1}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: getColorByKey(item?.scope),
                          color: "#ffff",
                        }}
                      >
                        <Iconify icon={item?.icon ?? defaultIcon} />
                      </Avatar>

                      <Box>
                        <Typography variant="h5" fontWeight={700}>
                          {item?.count}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {item?.name}
                        </Typography>
                        <Chip 
                          label={
                            <Typography variant="caption">
                              {item?.type ? "WORKFLOW" : "CARE"}
                            </Typography>
                          }
                          color="default"
                          size="small"
                        />
                      </Box>
                      <Tooltip title={translate('details.patient.transfer.goTo')}>
                        <IconButton
                          sx={{ ml: "auto" }}
                          onClick={() => 
                            window.open(getGoToSection(item?.type ?? item?.scope, patientId!), '_blank')
                          }
                        >
                          <Iconify icon="fluent:open-12-filled" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ))
                }
              </Box>
            </Box>
          }
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" color="text.secondary">
            Pending Confirmation for
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {patient?.indexedFullName}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button onClick={handlerClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmTransfer}
            disabled={!patient || !orgSelected}
          >
            {translate('details.patient.transfer.buttonTransfer')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default TransferManagingOrg;